import React, { useEffect, useState } from "react";
import { ChevronRight, PartyPopper, User } from "lucide-react";
import { IntegrationIssue, IntegrationIssueStatus } from "../IntegrationsManagementEntities";
import { useHistory } from "react-router-dom";
import { getCapitalizedTimeFromNow } from "../../shared/utils/SharedComponentUtils";
import { ISSUES_PATH, navigateToIndividualIssuePage } from "../../../router/RouterUtils";
import IntegrationNameAndLogo from "../../shared/IntegrationNameAndLogo";
import {
  Button,
  ButtonVariant,
  Text,
  Card,
  SkeletonLoader,
} from "@merge-api/merge-javascript-shared";

interface Props {
  integrationIssues: IntegrationIssue[] | undefined;
}

const DashboardIssues = ({ integrationIssues }: Props) => {
  const numDisplayIssues = 3;
  const history = useHistory();
  const [displayIntegrationIssues, setDisplayIntegrationIssues] = useState<IntegrationIssue[]>([]);
  const [displayOngoing, setDisplayOngoing] = useState<boolean>(true);

  useEffect(() => {
    setDisplayIntegrationIssues(
      integrationIssues
        ?.filter(
          (integrationIssue: IntegrationIssue) =>
            integrationIssue.status == IntegrationIssueStatus.ONGOING,
        )
        .slice(0, numDisplayIssues) ?? [],
    );
  }, [integrationIssues]);

  return (
    <Card>
      <div className="p-4 border-b border-gray-10 flex flex-row justify-between items-center">
        <Text variant="h5">Recent issues</Text>
        <Button
          variant={ButtonVariant.TextBlue}
          onClick={() => history.push(ISSUES_PATH)}
          rightIcon={<ChevronRight size={16} />}
        >
          View all issues
        </Button>
      </div>

      <div className="flex flex-col p-4 gap-y-4">
        {integrationIssues && displayIntegrationIssues ? (
          displayIntegrationIssues.length > 0 ? (
            <>
              {displayIntegrationIssues.map((integrationIssue) => (
                <Card
                  variant="outline"
                  type="button"
                  className="flex flex-row justify-between items-center px-5 py-3 hover:bg-gray-0"
                  onClick={() => {
                    navigateToIndividualIssuePage(history, integrationIssue.id);
                  }}
                  key={integrationIssue.id}
                >
                  {/* Tile shows error_description (free text) + end_user.organization_name and is thus masked at the section */}
                  <div className="ph-no-capture flex flex-col gap-y-1">
                    <div className="flex flex-row gap-x-2 items-center flex-wrap">
                      <Text variant="h6" className="text-red-60 text-left">
                        {integrationIssue.error_description}
                      </Text>
                      {integrationIssue.last_incident_time && (
                        <Text variant="sm" className="text-gray-40 text-left">
                          {getCapitalizedTimeFromNow(integrationIssue.last_incident_time)}
                        </Text>
                      )}
                    </div>
                    <div className="flex flex-row gap-x-2 items-center">
                      <User size={12} className="flex-shrink-0" />

                      <Text className="text-left">
                        {integrationIssue.linked_account.end_user.organization_name}
                      </Text>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <IntegrationNameAndLogo
                      integration={integrationIssue.linked_account.integration}
                    />
                  </div>
                </Card>
              ))}
            </>
          ) : (
            <div className="flex flex-col items-center w-full py-12 text-gray-40 gap-y-4">
              <PartyPopper size={40} />
              <Text variant="h5">Hooray! No new issues</Text>
            </div>
          )
        ) : (
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <Card
                variant="outline"
                className="flex flex-row justify-between items-center px-5 py-3"
              >
                <div className="flex flex-col gap-y-1">
                  <SkeletonLoader height={14} width={230} borderRadius={4} />
                  <SkeletonLoader height={14} width={230} borderRadius={4} />
                </div>
                <SkeletonLoader height={14} width={100} borderRadius={4} />
              </Card>
            ))}
          </>
        )}
      </div>
    </Card>
  );
};

export default DashboardIssues;
